import { displayPopover, hidePopover } from './popover.js';

export type FlightDetails = {
    flight_number?: string;
    origin?: string;
    origin_iata?: string;
    destination?: string;
    destination_iata?: string;
    duration?: string;
    departures?: string[];
    arrivals?: string[];
    aircraft?: string[];
    aircraftName?: string[];
    businessSuite?: boolean[];
    date?: string;
}

export class FlightNumberElement extends HTMLElement {
    static observedAttributes: string[] = ['flight-number', 'origin-iata', 'destination-iata', 'start-time', 'end-time', 'popover-id'];
    #flightDetails: FlightDetails = new Object() as FlightDetails;
    #hasRendered = false;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback(attrName: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (attrName === 'popover-id') {
                this.popoverID = newValue;
            }
        }
    }

    connectedCallback() {
        const template = document.getElementById('flight-number-element-template') as HTMLTemplateElement;
        const node = document.importNode(template.content, true);
        this.shadowRoot!.appendChild(node);

        this.#hasRendered = true;
        if (this.flightDetails.flight_number) {
            this._addContent();
            this.setEventListeners();
        }
    }

    setEventListeners() {
        const flightNum = this;
        ['mouseenter', 'touchstart', 'touchmove'].forEach(event => {
            // Remove event listeners first
            flightNum.removeEventListener(event, flightNum.showFlightDetailsPopover)
            flightNum.addEventListener(event, flightNum.showFlightDetailsPopover.bind(flightNum), {passive: true});
        });

        ['mouseleave', 'touchend'].forEach(event => {
            flightNum.addEventListener(event, () => {hidePopover(flightNum.popoverID);});
        });
    }

    showFlightDetailsPopover(e: Event) {
        const flightNumberPopoverTemplate = document.getElementById('flight-number-popover-template') as HTMLTemplateElement;
        const flightNumberBodyTemplate = document.getElementById('flight-number-table-body-template') as HTMLTemplateElement;
        const flightNumberGroupBodyTemplate = document.getElementById('flight-number-group-table-body-template') as HTMLTemplateElement;

        const createContent = (popover: HTMLDivElement) => {
            const flightDetails: FlightDetails = this.flightDetails;
            const popoverNode = flightNumberPopoverTemplate.content.cloneNode(true) as HTMLTableElement;
            popoverNode.querySelector('[data-component="flight-number"]')!.textContent = flightDetails.flight_number || '';
            popoverNode.querySelector('[data-component="date"]')!.textContent = flightDetails.date || '';

            var tbody: HTMLTableSectionElement;
            if ((this.flightDetails.departures && this.flightDetails.departures.length > 1) || (this.flightDetails.arrivals && this.flightDetails.arrivals.length > 1)) {
                tbody = flightNumberGroupBodyTemplate.content.cloneNode(true) as HTMLTableSectionElement;

                // If departures and arrivals are the same, display only one value.
                var departures = '';
                if (flightDetails.departures && flightDetails.departures[0] == flightDetails.departures[1]) {
                    departures = flightDetails.departures[0];
                } else if (flightDetails.departures) {
                    departures = flightDetails.departures.join(' - ');
                }
                var arrivals = '';
                if (flightDetails.arrivals && flightDetails.arrivals[0] == flightDetails.arrivals[1]) {
                    arrivals = flightDetails.arrivals[0];
                } else if (flightDetails.arrivals) {
                    arrivals = flightDetails.arrivals.join(' - ');
                }

                tbody.querySelector('[data-component="departures"]')!.textContent = departures;
                tbody.querySelector('[data-component="arrivals"]')!.textContent = arrivals;

                if (flightDetails.duration) {
                    tbody.querySelector('[data-component="duration"]')!.textContent = flightDetails.duration;
                } else {
                    // Remove the duration row if no duration is provided
                    tbody.querySelector('[data-component="duration"]')!.parentElement!.remove();
                }
                var aircraftList = '';
                flightDetails.aircraft?.forEach((aircraft, index) => {
                    const aircraftName = flightDetails.aircraftName ? flightDetails.aircraftName[index] : aircraft;
                    const businessSuite = flightDetails.businessSuite ? flightDetails.businessSuite[index] : false;
                    aircraftList += ('<span>' + (aircraftName && businessSuite ? `${aircraftName} (Business Suite)` : aircraftName) + '<br/>');
                });

                if (aircraftList) {
                    tbody.querySelector('[data-component="aircraftNames"]')!.innerHTML = aircraftList;
                } else {
                    // Remove the aircraft row if no aircraft is provided
                    tbody.querySelector('[data-component="aircraftNames"]')!.parentElement!.remove();
                }

            } else {
                tbody = flightNumberBodyTemplate.content.cloneNode(true) as HTMLTableSectionElement;
                tbody.querySelector('[data-component="departures"]')!.textContent = flightDetails.departures ? flightDetails.departures[0] : '';
                tbody.querySelector('[data-component="arrivals"]')!.textContent = flightDetails.arrivals ? flightDetails.arrivals[0] : '';
                tbody.querySelector('[data-component="duration"]')!.textContent = flightDetails.duration || '';
                tbody.querySelector('[data-component="aircraft"]')!.textContent = flightDetails.aircraftName ? flightDetails.aircraftName[0] : '';
                tbody.querySelector('[data-component="businessSuite"]')!.textContent = flightDetails.businessSuite ? 'Business Suite' : '';
            }
            tbody.querySelector('[data-component="origin"]')!.textContent = flightDetails.origin || '';
            tbody.querySelector('[data-component="destination"]')!.textContent = flightDetails.destination || '';
            tbody.querySelectorAll('[data-component="origin-iata"]').forEach(el => el.textContent = `(${flightDetails.origin_iata})`);
            tbody.querySelectorAll('[data-component="destination-iata"]').forEach(el => el.textContent = `(${flightDetails.destination_iata})`);

            popoverNode.querySelector('tbody')!.appendChild(tbody);
            popover.innerHTML = ''; // Clear the inner HTML to avoid duplication
            popover.appendChild(popoverNode);
            return popover;
        }

        displayPopover((e.target as HTMLElement), createContent, this.popoverID, 'horizontal');
    }

    set flightDetails(flightDetails: FlightDetails) {
        this.#flightDetails = flightDetails;
        if (this.#hasRendered) {
            this._addContent();
            this.setEventListeners();
        }
    }

    get flightDetails(): FlightDetails {
        return this.#flightDetails;
    }

    set popoverID(value: string) {
        if (value) {
            this.setAttribute('popover-id', value);
        } else {
            this.removeAttribute('popover-id');
        }
    }

    get popoverID(): string {
        return this.getAttribute('popover-id') || '';
    }

    private _addContent() {
        const flightNumber = this.flightDetails.flight_number
        const origin = this.flightDetails.origin_iata
        const destination = this.flightDetails.destination_iata
        const departure = this.flightDetails.departures
        const arrival = this.flightDetails.arrivals

        const flightNumberSpan = this.shadowRoot!.getElementById('flight-number') as HTMLSpanElement;
        const originSpan = this.shadowRoot!.getElementById('origin-iata') as HTMLSpanElement;
        const destinationSpan = this.shadowRoot!.getElementById('destination-iata') as HTMLSpanElement;
        const startTimeSpan = this.shadowRoot!.getElementById('start-time') as HTMLSpanElement;
        const endTimeSpan = this.shadowRoot!.getElementById('end-time') as HTMLSpanElement;

        if (flightNumber) flightNumberSpan.textContent = flightNumber;
        if (flightNumber) flightNumberSpan.setAttribute('data-text', flightNumber);
        if (origin) originSpan.textContent = origin;
        if (origin) originSpan.setAttribute('data-text', origin);
        if (destination) destinationSpan.textContent = destination;
        if (destination) destinationSpan.setAttribute('data-text', destination);
        if (departure) startTimeSpan.textContent = departure.join(' - ');
        if (departure) startTimeSpan.setAttribute('data-text', departure.join(' - '));
        if (arrival) endTimeSpan.textContent = arrival.join(' - ');
        if (arrival) endTimeSpan.setAttribute('data-text', arrival.join(' - '));
    }
}
customElements.define('flight-number', FlightNumberElement);
